import {
  toCamelCase,
  WSButton,
  WSButtons,
  WSDrawer,
  WSElement,
  WSForm,
  WSLayout,
  WSRadioInputGroup,
  WSScreen,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import {
  useCollaboratorSettingsAdditionalData,
  useListCollaboratorSettingsAdditionalData
} from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { OverlaySpinner } from "../../../components/OverlaySpinner";
import styles from "./InvoicesCollaboratorsCustomFieldsDetails.module.scss";
import {
  IAdditionalData,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import {
  useDeleteCollaboratorSettingsAdditionalData,
  useUpdateCollaboratorSettingsAdditionalData
} from "../../../query/payments/mutations";

export const InvoicesCollaboratorsCustomFieldsDetails: React.FC<RouteComponentProps<{
  additionalDataId: string;
}>> = ({ match, location }) => {
  const history = useHistory();
  const qAdditionalData = useCollaboratorSettingsAdditionalData(
    match.params.additionalDataId
  );

  return (
    <WSQueries
      queries={{ qAdditionalData }}
      renderLoader={() => (
        <>
          <WSScreen.Mobile>
            <OverlaySpinner />
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.goBack();
              }}
              size="S"
              noCloseIcon
            >
              <WSElement className={styles.main}>
                <OverlaySpinner />
              </WSElement>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    >
      {({ qAdditionalData: { data: additionalData } }) => (
        <>
          <WSScreen.Mobile>
            <WSLayout
              title={additionalData.name}
              onBack={() => {
                history.goBack();
              }}
              line
            >
              <WSElement className={styles.main}>
                <Inner data={additionalData} />
              </WSElement>
            </WSLayout>
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.goBack();
              }}
              size="S"
              noCloseIcon
            >
              <WSLayout
                title={additionalData.name}
                onClose={() => {
                  history.goBack();
                }}
                line
                smallHeader
              >
                <WSElement className={styles.main}>
                  <Inner data={additionalData} />
                </WSElement>
              </WSLayout>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    </WSQueries>
  );
};

export const Inner: React.FC<{ data: IAdditionalData & { id: string } }> = ({
  data
}) => {
  const qListAdditionalData = useListCollaboratorSettingsAdditionalData();

  const history = useHistory();
  const [
    updateData,
    updateDataMeta
  ] = useUpdateCollaboratorSettingsAdditionalData();

  const [
    deleteData,
    deleteDataMeta
  ] = useDeleteCollaboratorSettingsAdditionalData();

  return (
    <>
      <WSForm
        defaultValues={{
          name: data.name,
          fieldType: data.type,
          required: data.required ? "yes" : "no"
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .notOneOf(
              qListAdditionalData?.data
                ?.map(a => a.name)
                .filter(name => name !== data.name) || [],
              "A custom field with this name already exists"
            )
            .required("Name is required"),
          fieldType: Yup.string(),
          required: Yup.string()
        })}
        onSubmit={async formData => {
          await updateData(
            {
              id: data.id,
              data: {
                name: formData.name,
                required: !!formData.required,
                type: formData.fieldType,
                key: toCamelCase(formData.name)
              }
            },
            {
              onSuccess() {
                history.goBack();
              }
            }
          );
        }}
      >
        <WSForm.Field
          mb="M"
          label="Field name"
          name="name"
          component={WSTextInput}
        />

        <WSForm.Field
          mb="L"
          label=" "
          name="required"
          component={WSRadioInputGroup}
          componentProps={{
            options: [
              {
                label: "Required",
                value: "yes"
              },
              {
                label: "Optional",
                value: "no"
              }
            ]
          }}
        />

        <WSElement className={styles.disabled}>
          <WSForm.Label mb="S">Field type</WSForm.Label>
          <WSRadioInputGroup
            value={data.type}
            options={[
              {
                label: "Text field",
                value: WSDataTypes.String
              },
              {
                label: "Checkbox",
                value: WSDataTypes.Boolean
              }
            ]}
            name="disabled"
          />
          <WSText.Label mt="S" color="grey600">
            Field type cannot be edited for existing fields.
          </WSText.Label>
        </WSElement>

        <WSErrorMessage
          mt="M"
          error={updateDataMeta.error || deleteDataMeta.error}
          contextKey="CreateCollaborator"
        />

        <WSButtons mt="XL" forceFullWidth>
          <WSForm.SubmitButton data-testid="submit">
            Update field
          </WSForm.SubmitButton>
          <WSButton.Destructive
            type="button"
            onAsyncClick={async () => {
              await deleteData(data.id);
              history.goBack();
            }}
          >
            Delete field
          </WSButton.Destructive>
        </WSButtons>
      </WSForm>
    </>
  );
};
